import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
const { FiX, FiBarChart2, FiCopy, FiStar, FiLock, FiType, FiClock } = FiIcons;
import SafeIcon from '../common/SafeIcon';

export default function InsightsModal({ snippets, usageCounts, onClose }) {
  const stats = useMemo(() => {
    const active = (snippets || []).filter(s => !s.isDeleted);
    const uses = active.map(s => ({
      ...s,
      uses: Math.max(Number(usageCounts[s.id] || 0), Number(s.usageCount || 0))
    }));
    const totalCopies = uses.reduce((sum, s) => sum + s.uses, 0);
    const mostUsed = [...uses].sort((a, b) => b.uses - a.uses).slice(0, 5);
    const unused = uses.filter(s => s.uses === 0).length;
    return {
      active: active.length,
      totalCopies,
      pinned: active.filter(s => s.isPinned).length,
      permanent: active.filter(s => s.isPermanent).length,
      templates: active.filter(s => /\{\{\s*[a-zA-Z0-9_]+\s*\}\}/.test(s.content || '')).length,
      unused,
      mostUsed
    };
  }, [snippets, usageCounts]);

  const cards = [
    ['Snippets', stats.active, FiType],
    ['Copies', stats.totalCopies, FiCopy],
    ['Pinned', stats.pinned, FiStar],
    ['Permanent', stats.permanent, FiLock],
    ['Templates', stats.templates, FiType],
    ['Never used', stats.unused, FiClock]
  ];

  return (
    <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}} className="fixed inset-0 z-[180] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <motion.div initial={{scale:.96,opacity:0}} animate={{scale:1,opacity:1}} exit={{scale:.96,opacity:0}} className="w-full max-w-2xl overflow-hidden rounded-3xl border border-gray-700 bg-gray-900 shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-800 p-6">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-indigo-500/10 p-2 text-indigo-400"><SafeIcon icon={FiBarChart2} /></div>
            <div>
              <h2 className="text-xl font-bold text-white">Library Insights</h2>
              <p className="text-xs text-gray-500">Local usage signals from this device.</p>
            </div>
          </div>
          <button onClick={onClose} className="rounded-full p-2 text-gray-500 hover:bg-gray-800 hover:text-white"><SafeIcon icon={FiX} /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 p-6 sm:grid-cols-3">
          {cards.map(([label, value, icon]) => (
            <div key={label} className="rounded-2xl border border-gray-800 bg-gray-950/50 p-4">
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-gray-500"><SafeIcon icon={icon} /> {label}</div>
              <div className="mt-2 text-2xl font-black text-white">{value}</div>
            </div>
          ))}
        </div>

        <div className="border-t border-gray-800 p-6">
          <div className="mb-3 text-xs font-bold uppercase tracking-widest text-gray-500">Most used</div>
          <div className="space-y-2">
            {stats.mostUsed.map((snippet, index) => (
              <div key={snippet.id} className="flex items-center justify-between rounded-xl border border-gray-800 bg-gray-950/40 px-4 py-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-gray-200">{index + 1}. {snippet.title || 'Untitled Snippet'}</div>
                  <div className="text-[10px] text-gray-500">{snippet.type || 'plain'}</div>
                </div>
                <div className="shrink-0 rounded-full bg-indigo-500/10 px-3 py-1 text-xs font-bold text-indigo-300">{snippet.uses} copies</div>
              </div>
            ))}
            {stats.mostUsed.length === 0 && <div className="py-8 text-center text-sm text-gray-500">No usage data yet.</div>}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}